import {
  select,
  put,
  delay,
  takeLatest,
  takeEvery,
} from 'redux-saga/effects';
import RNFetchBlob from '../libraries/RNFetchBlob';
import {
  URL,
  SELFIE_STATE,
  SELFIE_STATE_ON_DELAY,
  SELFIE_ANALYSIS_DELAYED_TIME,
  UPLOAD_NOTIFICATION_TEXT,
  NOTIFICATION_IDENTIFIERS,
  NOTIFICATION_TYPE,
  COUNTRY_CODE,
  API_ERRORS,
  API_DOMAIN,
  SELFIE_NOTIFICATION_DEEPLINKS,
} from '../config/Constants';
import {
  saveCDNlinktoRedux,
  userFacialProperties,
  saveFaceAnalysisData,
} from '../actions/LoginActions';
import Utility from '../utils/Utility';
import {
  UPLOAD_SELFIE,
  UPLOAD_FND_IMAGE,
  FETCH_HISTORY_REQUEST,
  FETCH_SELFIE_ATTRIBUTES,
  saveSelfieData,
  FETCH_SELFIE_HISTROY_ATTRIBUTES,
  saveSelfieImage,
} from '../actions/SkinSelfieAnalysisActions';
import SelfieUtilities from '../utils/SelfieUtilities';
import {
  AnalyticsManager,
  EventType,
  EventParameterKey,
  EventParameterValue,
} from '../analytics';

import { getApiHeaders, convertJsonFromResponse } from './GeneratorUtil';
import AnalyticsUtility from '../analytics/AnalyticsUtility';
import CameraUtils from '../utils/CameraUtils';
import NotificationManager from '../utils/NotificationsManager';
import Config from 'react-native-config';

export function* startSelfieAnalysis() {
  yield put(
    saveFaceAnalysisData({
      analysis_progress: 0,
      analysis_stage: SELFIE_STATE.STARTING,
      show_progress_around_pic: true,
      selfie_upload_failure: false,
    }),
  );
}

export function* selfieAnalysisFailed(
  analysisPopupRequired,
  isUploadFailure = false,
) {
  const selfieUploadFailure = isUploadFailure
    ? { selfie_upload_failure: true }
    : {};
  yield put(
    saveFaceAnalysisData({
      analysis_progress: 0,
      popover_state: analysisPopupRequired ? 'show' : 'hidden',
      analysis_stage: SELFIE_STATE.ERROR,
      show_progress_around_pic: false,
      ...selfieUploadFailure,
    }),
  );
}

const uploadSelfieImageToFoxy = async (base64Data, headers) => {
  const uploadUrl = `${API_DOMAIN}/api/v1/selfies`;
  const requestBody = {
    selfie: {
      image: base64Data.base64_selfie_image,
    },
  };
  const { data } = await RNFetchBlob.fetch(
    'POST',
    uploadUrl,
    { ...headers },
    JSON.stringify(requestBody),
    // formData,
  ).uploadProgress({ interval: 50 }, (written, total) => {
    // NotificationManager.setProgress(total, written);
  });

  if (data) {
    console.tron.log('-------skin selfie upload response----', data);
    return Utility.jsonParser(data);
  }

  return undefined;
};

export function* uploadBase64Data(data) {
  const headers = yield getApiHeaders();
  for (let i = 0; i < 4; i++) {
    try {
      const response = yield uploadSelfieImageToFoxy(data, headers);

      if (response) {
        return response;
      }
      AnalyticsManager.logEvent(EventType.onboardingEvent.SELFIE_UPLOAD_RETRY, {
        [EventParameterKey.RETRY_COUNT]: i + 1,
        [EventParameterKey.RAW_ERROR]: 'server_error',
        [EventParameterKey.SOURCE]: EventParameterValue.SOURCE.AUTOMATIC,
        [EventParameterKey.STATUS_CODE]: response.status,
      });
    } catch (error) {
      AnalyticsManager.logEvent(EventType.onboardingEvent.SELFIE_UPLOAD_RETRY, {
        [EventParameterKey.RETRY_COUNT]: i + 1,
        [EventParameterKey.RAW_ERROR]: 'network_error',
        [EventParameterKey.SOURCE]: EventParameterValue.SOURCE.AUTOMATIC,
      });
    }

    yield delay(500);
  }

  NotificationManager.retryUpload();
  AnalyticsManager.logEvent(
    EventType.onboardingEvent.SELFIE_UPLOAD_RETRY_EXHAUSTED,
  );
  return undefined;
}

export function* uploadSkinImage(action) {
  let selfieAnalysisCompleted = false;
  let currentState = '';
  const selfieAnalysisStartedAt = new Date();
  const { base64Data, analysisReviewPopupRequired, callback } = action;
  try {
    const userInfo = yield select((state) => state.UserAccountInfo);
    const { authToken, mobileNumber, callingCode } = userInfo;
    yield put(
      saveSelfieData({
        uploadedSelfieStatus: 'uploading',
        analysis_progress: 0,
        analysis_stage: SELFIE_STATE.STARTING,
        show_progress_around_pic: true,
      }),
    );
    const postData = {
      base64_selfie_image: base64Data,
    };

    const payload = {
      title: UPLOAD_NOTIFICATION_TEXT.IMAGE_UPLOAD_TITLE,
      description: UPLOAD_NOTIFICATION_TEXT.IMAGE_UPLOAD_DESCRIPTION,
      type: NOTIFICATION_TYPE.PROFILE_PIC_UPLOAD,
      isLiveNotification: true,
      notificationTitle: UPLOAD_NOTIFICATION_TEXT.IMAGE_UPLOAD_TITLE,
      notificationDescription:
        UPLOAD_NOTIFICATION_TEXT.IMAGE_UPLOAD_DESCRIPTION,
      notificationFallbackTitle: UPLOAD_NOTIFICATION_TEXT.IMAGE_UPLOAD_TITLE,
      notificationFallbackDescription:
        UPLOAD_NOTIFICATION_TEXT.IMAGE_UPLOAD_DESCRIPTION,
      deepLink: SELFIE_NOTIFICATION_DEEPLINKS['upload_selfie'],
      state: 'upload_selfie',
      progressColor: Config.LIVE_ACTIVITY_PROGRESS_BAR_COLOR,
    };
    NotificationManager.createNotification(
      payload,
      NOTIFICATION_IDENTIFIERS.PROFILE_PIC_UPLOAD,
    );
    NotificationManager.setProgress(
      UPLOAD_NOTIFICATION_TEXT.IMAGE_UPLOAD_TITLE,
      UPLOAD_NOTIFICATION_TEXT.IMAGE_UPLOAD_DESCRIPTION,
      100,
      25,
      SELFIE_NOTIFICATION_DEEPLINKS['upload_selfie'],
    );

    yield startSelfieAnalysis();

    // FIXME: There is no timeout or retry.

    const timer = setTimeout(() => {
      if (!selfieAnalysisCompleted) {
        AnalyticsManager.logEvent(
          EventType.onboardingEvent.SELFIE_ANALYSIS_DELAY,
          { [EventParameterKey.STATE]: currentState },
        );
      }
      clearTimeout(timer);
    }, SELFIE_ANALYSIS_DELAYED_TIME);
    currentState = SELFIE_STATE_ON_DELAY.IMAGE_UPLOAD_START;
    AnalyticsUtility.logSelfieState(currentState);

    const response = yield uploadBase64Data(postData);

    if (response) {
      callback(response);
      const { selfie: { id = '', image_url = '' } = {} } = response;
      currentState = SELFIE_STATE_ON_DELAY.IMAGE_UPLOAD_DONE;
      NotificationManager.removeProgressBar();
      AnalyticsUtility.logSelfieState(currentState);
      AnalyticsManager.logEvent(
        EventType.onboardingEvent.SELFIE_UPLOAD_SUCCESS,
        {
          [EventParameterKey.TIME_TAKEN]: Utility.getTimeDiff(
            new Date(),
            selfieAnalysisStartedAt,
          ),
        },
      );
      yield put(saveCDNlinktoRedux(image_url));
      yield put(
        saveFaceAnalysisData({
          analysis_progress: 50,
          analysis_stage: SELFIE_STATE.SELFIE_UPLOADED,
          image_id: id,
          selfie_image: image_url,
        }),
      );
      yield put(
        saveSelfieData({
          selfie_image: image_url,
          analysis_progress: 50,
          analysis_stage: SELFIE_STATE.SELFIE_UPLOADED,
          show_progress_around_pic: true,
        }),
      );
      yield put(saveSelfieImage({ selfie_url: image_url }));
      //TODO:Extract Code from Here Step 1
      currentState = SELFIE_STATE_ON_DELAY.WAITING_FOR_USER_ATTRIBUTES;
      yield fetchSkinAttributes({
        image_id: id,
      });
    } else {
      AnalyticsManager.logEvent(
        EventType.onboardingEvent.SELFIE_UPLOAD_FAILURE,
        {
          [EventParameterKey.TIME_TAKEN]: Utility.getTimeDiff(
            new Date(),
            selfieAnalysisStartedAt,
          ),
        },
      );
      yield selfieAnalysisFailed(analysisReviewPopupRequired, true);
    }
  } catch (error) {
    AnalyticsManager.logEvent(
      EventType.onboardingEvent.SELFIE_ANALYSIS_FAILURE,
      {
        [EventParameterKey.STATE]: currentState,
        [EventParameterKey.RAW_ERROR]: error,
        [EventParameterKey.TIME_TAKEN]: Utility.getTimeDiff(
          new Date(),
          selfieAnalysisStartedAt,
        ),
      },
    );
    yield selfieAnalysisFailed(analysisReviewPopupRequired);
  }
}

export function* fetchSkinAttributes(action) {
  const { mobileNumber } = yield select((state) => state.UserAccountInfo);
  const { image_id, callback } = action;

  const MAX_RETRIES = 15;
  const RETRY_DELAY = 5000;
  const skinAnalysisRetryFailure = "Failed to retrieve skin analysis data after multiple attempts.";
  const skinAnalysisErrorMessage = "Failed to retrieve skin analysis data.";

  let retryCount = 0;
  let selfieAnalysisStatusCode;

  const payload = {
    title: UPLOAD_NOTIFICATION_TEXT.TAKE_SURVEY_TITLE,
    description: UPLOAD_NOTIFICATION_TEXT.TAKE_SURVEY_DESCRIPTION,
    type: NOTIFICATION_TYPE.PROFILE_PIC_UPLOAD,
    isLiveNotification: true,
    notificationTitle: UPLOAD_NOTIFICATION_TEXT.TAKE_SURVEY_TITLE,
    notificationDescription:
      UPLOAD_NOTIFICATION_TEXT.TAKE_SURVEY_DESCRIPTION,
    notificationFallbackTitle:
      UPLOAD_NOTIFICATION_TEXT.TAKE_SURVEY_TITLE,
    notificationFallbackDescription:
      UPLOAD_NOTIFICATION_TEXT.TAKE_SURVEY_DESCRIPTION,
    deepLink: SELFIE_NOTIFICATION_DEEPLINKS['take_survey'],
    state: 'take_survey',
    progressColor: Config.LIVE_ACTIVITY_PROGRESS_BAR_COLOR,
  };

  NotificationManager.createNotification(
    payload,
    NOTIFICATION_IDENTIFIERS.PROFILE_PIC_UPLOAD,
  );

  NotificationManager.setProgress(
    UPLOAD_NOTIFICATION_TEXT.TAKE_SURVEY_TITLE,
    UPLOAD_NOTIFICATION_TEXT.TAKE_SURVEY_DESCRIPTION,
    100,
    50,
    SELFIE_NOTIFICATION_DEEPLINKS['take_survey'],
  );

  while (retryCount < MAX_RETRIES) {
    try {
      let url = `${API_DOMAIN}/api/v1/selfies`;
      let queryString = '';

      if (Utility.isPresent(mobileNumber)) {
        queryString = '?';
        if (Utility.isPresent(image_id)) {
          queryString = `/${image_id}`;
        } else {
          queryString = `${queryString}user[phone_number]=${mobileNumber}&user[country_code]=${COUNTRY_CODE}`;
        }
        url = `${url}${queryString}`;
        if (Utility.isPresent(image_id)) {
          url = `${url}?sad_id=${image_id}`;
        }
      } else {
        // Case for guest user
        if (Utility.isPresent(image_id)) {
          url = `${url}/${image_id}`;
        }
      }
      const response = yield fetch(url, {
        method: 'GET',
        headers: yield getApiHeaders(),
      });
      const jsonResponse = yield convertJsonFromResponse(response);
      const selfieStatus = jsonResponse.data.attributes.status;
      const statusCode = response.status;

      if (statusCode === 200) {
        if (selfieStatus === 'completed') {
          const payload = {
            title: UPLOAD_NOTIFICATION_TEXT.REVIEW_SELFIE_TITLE,
            description: UPLOAD_NOTIFICATION_TEXT.REVIEW_SELFIE_DESCRIPTION,
            type: NOTIFICATION_TYPE.PROFILE_PIC_UPLOAD,
            isLiveNotification: true,
            notificationTitle: UPLOAD_NOTIFICATION_TEXT.REVIEW_SELFIE_TITLE,
            notificationDescription: UPLOAD_NOTIFICATION_TEXT.REVIEW_SELFIE_DESCRIPTION,
            notificationFallbackTitle: UPLOAD_NOTIFICATION_TEXT.REVIEW_SELFIE_TITLE,
            notificationFallbackDescription: UPLOAD_NOTIFICATION_TEXT.REVIEW_SELFIE_DESCRIPTION,
            deepLink: SELFIE_NOTIFICATION_DEEPLINKS['review_selfie'],
            state: 'review_selfie',
            progressColor: Config.LIVE_ACTIVITY_PROGRESS_BAR_COLOR,
          };
          NotificationManager.createNotification(
            payload,
            NOTIFICATION_IDENTIFIERS.PROFILE_PIC_UPLOAD,
          );
          NotificationManager.setProgress(
            UPLOAD_NOTIFICATION_TEXT.REVIEW_SELFIE_TITLE,
            UPLOAD_NOTIFICATION_TEXT.REVIEW_SELFIE_DESCRIPTION,
            100,
            100,
            SELFIE_NOTIFICATION_DEEPLINKS['review_selfie'],
          );
          yield put(
            saveSelfieData({
              my_attributes_values: jsonResponse,
              uploadedSelfieStatus: selfieStatus,
              analysis_progress: 100,
              analysis_stage: SELFIE_STATE.COMPLETED,
              show_progress_around_pic: false,
            }),
          );
          // Break the loop on successful response
          const score = jsonResponse?.data?.attributes?.skin_score;
          AnalyticsManager.logEvent(EventType.onboardingEvent.SELFIE_ANALYSIS_SUCCESS,{
            [EventParameterKey.SCORE]: score
          })
          return;
        } else if (selfieStatus === 'failed') {
          console.tron.log('Selfie status failed');
          NotificationManager.cancelNotification(NOTIFICATION_IDENTIFIERS.PROFILE_PIC_UPLOAD)
          AnalyticsManager.logEvent(EventType.onboardingEvent.SELFIE_ANALYSIS_ERROR, {
            [EventParameterKey.STATUS_CODE]: statusCode,
            [EventParameterKey.RAW_ERROR]: skinAnalysisErrorMessage,
          });
          yield put(
            saveSelfieData({
              uploadedSelfieStatus: selfieStatus,
              analysis_stage: SELFIE_STATE.FAILED,
              show_progress_around_pic: false,
            }),
          );
          return;
        } else {
          console.tron.log(`Unexpected selfie status: ${selfieStatus}`);
        }
      } else if (statusCode === 404) {
        selfieAnalysisStatusCode = statusCode;
        retryCount++;
        yield delay(RETRY_DELAY);
      } else {
        console.tron.log(`Unexpected status code: ${statusCode}`);
      }
    } catch (error) {
      console.tron.log('Error', `${error}`);
      if (retryCount >= MAX_RETRIES) {
        console.tron.log('Max retries reached');
        break;
      }
      retryCount++;
      yield delay(RETRY_DELAY);
    }
  }
  AnalyticsManager.logEvent(EventType.onboardingEvent.SELFIE_ANALYSIS_ERROR, {
    [EventParameterKey.STATUS_CODE]: selfieAnalysisStatusCode,
    [EventParameterKey.RAW_ERROR]: skinAnalysisRetryFailure,
  });
  NotificationManager.cancelNotification(NOTIFICATION_IDENTIFIERS.PROFILE_PIC_UPLOAD);
  yield put(
    saveSelfieData({
      uploadedSelfieStatus: 'failed',
      analysis_stage: SELFIE_STATE.FAILED,
      show_progress_around_pic: false,
    }),
  );
  console.tron.log('Polling ended without success');
}

export function* mapMyAttributesWithMasterList(
  myAttributes,
  captureEvent = false,
) {
  try {
    const master_attribute_list = yield select(
      (state) => state.facialAnalysis.master_attribute_list,
    );

    const userGender = SelfieUtilities.extractUserGender(
      myAttributes,
      master_attribute_list,
    );

    const isSelfieReviewed =
      SelfieUtilities.detectIfSelfieIsReviewed(myAttributes);

    const listUserSuitableAttributes =
      SelfieUtilities.getListOfUserSuitableAttributes(
        userGender,
        master_attribute_list,
      );

    const userAttributes = SelfieUtilities.addUserSelectedValuesToAttributes(
      myAttributes,
      listUserSuitableAttributes,
    );
    yield put(userFacialProperties(userAttributes));
    if (captureEvent) {
      SelfieUtilities.logAttributesDetectionEvent(userAttributes);
    }
    return userGender;
  } catch (err) {}
}

function* uploadFndImageToFoxy(action) {
  const { imageUrl } = action;
  const base64Data = yield CameraUtils.convertImageToBase64(imageUrl);
  try {
    const userInfo = yield select((state) => state.UserAccountInfo);
    const { authToken, mobileNumber, callingCode } = userInfo;
    const postData = {
      auth_token: authToken,
      user: {
        country_code: callingCode,
        phone_number: mobileNumber,
        base64_selfie_image: `data:image/jpeg;base64,${base64Data}`,
      },
    };
    const response = yield fetch(URL.UPLOAD_BASE_64_IMAGE, {
      method: 'POST',
      headers: yield getApiHeaders(),
      body: JSON.stringify(postData),
    });

    if (response.status >= 200 && response.status < 300) {
      AnalyticsManager.logEvent(EventType.onboardingEvent.FND_IMAGE_UPLOAD);
    } else {
      AnalyticsManager.logEvent(
        EventType.onboardingEvent.FND_IMAGE_UPLOAD_FAILURE,
        {
          [EventParameterKey.STATUS_CODE]: response.status,
        },
      );
    }
  } catch (error) {
    AnalyticsManager.logEvent(
      EventType.onboardingEvent.FND_IMAGE_UPLOAD_FAILURE,
      {
        [EventParameterKey.STATUS_CODE]: 0,
        [EventParameterKey.RAW_ERROR]: JSON.stringify(error),
      },
    );
  }
}

function* fetchHistorySaga(action) {
  const { SERVER_ERROR } = API_ERRORS;
  const { callback } = action;
  const { startDate, endDate, has_orbo_content } = action.payload;
  const url = `${API_DOMAIN}/api/v1/selfies?order=desc&page=0&start_date=${startDate}&end_date=${endDate}&has_orbo_content=${has_orbo_content}`;
  const result = yield fetch(url, {
    method: 'GET',
    headers: yield getApiHeaders(),
  });
  try {
    const details = yield result.json();
    if (result.status >= 200 && result.status < 300) {
      callback(true, details);
    } else {
      callback(false, {
        error: SERVER_ERROR,
        list: details,
        statusCode: result.status,
      });
    }
  } catch (error) {
    console.tron.log('------>error', `${error}`);
    callback(false, { error: SERVER_ERROR });
  }
}

function* fetchSelfieHistroyAttributes(action) {
  const { SERVER_ERROR } = API_ERRORS;
  const { callback } = action;
  const { startDate, endDate, has_orbo_content } = action.payload;
  const url = `${API_DOMAIN}/api/v1/selfies?start_date=${startDate}&end_date=${endDate}&order=desc&page=0&has_orbo_content=${has_orbo_content}`;
  try {
    const result = yield fetch(url, {
      method: 'GET',
      headers: yield getApiHeaders(),
    });
    const details = yield result.json();
    if (result.status >= 200 && result.status < 300) {
      callback(true, details);
    } else {
      callback(false, {
        error: SERVER_ERROR,
        list: details,
        statusCode: result.status,
      });
    }
  } catch (error) {
    callback(false, { error: SERVER_ERROR });
  }
}

export default function* watchGetSkinSelfieUploadSaga() {
  yield takeLatest(UPLOAD_SELFIE, uploadSkinImage);
  yield takeEvery(UPLOAD_FND_IMAGE, uploadFndImageToFoxy);
  yield takeEvery(FETCH_HISTORY_REQUEST, fetchHistorySaga);
  yield takeLatest(FETCH_SELFIE_ATTRIBUTES, fetchSkinAttributes);
  yield takeEvery(FETCH_SELFIE_HISTROY_ATTRIBUTES,fetchSelfieHistroyAttributes);
}
